import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { getInstagramAuthUrl } from "../../../services/facebookOnboarding.api";
import { getChannelAvailability } from "../../../services/platformIntegrations.api";
import { PROVIDERS } from "../../../constants/whatsappProviders";

/**
 * Canales que se pueden dar de alta, en el orden en que se ofrecen.
 *
 * WHY una lista y no un bloque JSX por canal: con la lista, añadir un canal es una entrada
 * más y el logo lo resuelve `ChannelIcon` desde el catálogo de proveedores. El título y la
 * descripción salen de `connections.addDialog.channels.<key>`; `tint` es el color de marca
 * con el que se tiñe su tarjeta en el selector (doc 0447, D7).
 *
 * Instagram entra SOLO por Business Login. La alta manual con token de página se retiró:
 * ofrecía dos caminos al mismo canal y el manual no recibe DMs reales (doc 0373).
 *
 * "Formulario Web" (doc 0417) NO abre el modal de QR/token de las conexiones
 * normales: abre su propio modal (`WebFormModal`, vía `onAddWebForm`).
 */
export const CHANNEL_OPTIONS = [
  {
    key: "baileys",
    provider: PROVIDERS.BAILEYS,
    tint: "#25D366",
    onSelect: ({ onAdd }) => onAdd("baileys"),
  },
  {
    key: "waba",
    provider: PROVIDERS.WABA,
    tint: "#128C7E",
    onSelect: ({ onAdd }) => onAdd("waba"),
  },
  {
    key: "facebook",
    provider: PROVIDERS.FACEBOOK,
    tint: "#1877F2",
    onSelect: ({ onAdd }) => onAdd("facebook"),
  },
  {
    key: "instagram",
    provider: PROVIDERS.INSTAGRAM,
    tint: "#E1306C",
    // Necesita la app de Instagram de la PLATAFORMA (Configuración → Integraciones, doc 0440).
    platform: "instagram",
    onSelect: async ({ user }) => {
      try {
        const { data } = await getInstagramAuthUrl();
        if (data?.url) {
          window.location.href = data.url;
        } else {
          toast.error("No se pudo abrir la conexión con Instagram. Inténtalo de nuevo en unos minutos.");
        }
      } catch (e) {
        // Red de seguridad: el selector ya no ofrece Instagram sin la app de la plataforma (0440), pero
        // si se desconfigura con la página abierta, nunca se nombra un fichero del servidor.
        const codigo = e?.response?.data?.error;
        if (codigo === "ERR_INSTAGRAM_APP_NOT_CONFIGURED") {
          toast.warn(
            user?.super
              ? "Instagram aún no está configurado. Hazlo en Configuración → Integraciones."
              : "Instagram no está disponible en esta plataforma.",
            { autoClose: 10000 }
          );
        } else {
          toast.error(
            "No se pudo iniciar la conexión con Instagram. Inténtalo de nuevo; si continúa, contacta con el administrador de la plataforma."
          );
        }
      }
    },
  },
  {
    // Cuenta PERSONAL por QR (doc 0438 Parte V). El alta por token de bot (modo bot) ya no se ofrece.
    key: "telegram",
    provider: PROVIDERS.TELEGRAM_USER,
    tint: "#229ED9",
    // Necesita la aplicación de Telegram de la PLATAFORMA (Configuración → Integraciones, doc 0440).
    platform: "telegram",
    onSelect: ({ onAdd }) => onAdd(PROVIDERS.TELEGRAM_USER),
  },
  {
    // Buzón de la empresa como canal de conversación (doc 0445). Sin app de plataforma: Gmail con contraseña de aplicación.
    key: "email",
    provider: PROVIDERS.EMAIL,
    tint: "#EA4335",
    onSelect: ({ onAdd }) => onAdd(PROVIDERS.EMAIL),
  },
  {
    key: "webform",
    provider: PROVIDERS.WEBFORM,
    tint: "#6366F1",
    onSelect: ({ onAddWebForm }) => onAddWebForm(),
  },
];

/** Qué canales de plataforma están listos (doc 0440). Ante error, ninguno: nunca se ofrece un canal que el backend rechazará. */
const useChannelAvailability = () => {
  const [disponibles, setDisponibles] = useState({});
  useEffect(() => {
    let vivo = true;
    getChannelAvailability()
      .then(({ data }) => vivo && setDisponibles(data || {}))
      .catch(() => vivo && setDisponibles({}));
    return () => {
      vivo = false;
    };
  }, []);
  return disponibles;
};

/**
 * Canales que ESTE usuario puede dar de alta. Sin la credencial de plataforma: el usuario normal
 * no ve el canal; el superadmin lo ve marcado `sinConfigurar` y le lleva a Integraciones. Nadie
 * vuelve a tropezar con un aviso del .env.
 */
export const useAddableChannels = (user) => {
  const history = useHistory();
  const disponibles = useChannelAvailability();
  return CHANNEL_OPTIONS.filter((o) => !o.platform || disponibles[o.platform] || user?.super).map((o) =>
    !o.platform || disponibles[o.platform]
      ? o
      : {
          ...o,
          sinConfigurar: true,
          onSelect: () => history.push(`/settings?tab=integrations&canal=${o.platform}`),
        }
  );
};
